/**
 * The nine-portal carousel, ported from the homepage on `main`.
 *
 * A sticky section as tall as nine screens-worth of scroll, inside which a
 * horizontal strip of portal cards is driven by scroll progress: the card
 * nearest the centre scales up, brightens and reveals its detail, and the rest
 * recede.
 *
 * ─── Surface ──────────────────────────────────────────────────────────────
 * Restyled into the dcv2 canvas language that /solutions — its only consumer —
 * now speaks. It previously arrived on a white ground under a violet/lavender/
 * pink gradient headline while the page around it was drawn in warm paper, ink
 * and cyan, so it read as a section lifted from another site.
 *
 * The interaction is untouched: same nine portals in the same order, the same
 * 540vh runway, the same springs, the same focal maths, the same reduced-motion
 * fallback. Only the paint changed. Specifically:
 *
 * - Tailwind classes and the `--landing-*` custom properties are gone. Every
 *   rule is an inline style resolving against the eleven dcv2 palette tokens,
 *   so the section themes with the page rather than against it.
 * - The nine per-portal hues are gone. The canvas has exactly one accent, so
 *   the portals are told apart the way its capability grid tells its cards
 *   apart: a mono index numeral 01–09, a status dot that alternates between a
 *   disc and a square and pulses on its own period, and position in the strip.
 *   Nothing is now distinguished by colour alone that was not already
 *   distinguished by its name, its department and its icon.
 * - The violet grid field became the dark sections' dot field, drawn in ink
 *   alpha; it still parallaxes off the same MotionValue.
 *
 * One thing was added rather than ported: a reduced-motion path. The original
 * has none, and scroll-jacking 540vh of page is precisely the interaction
 * `prefers-reduced-motion` exists to opt out of. Under that setting the
 * section collapses to its natural height, the strip becomes an ordinary
 * horizontally-scrollable row, and every card renders at full scale with its
 * detail already open — the same nine portals, reachable without the ride.
 *
 * Renamed from BentoGrid on the way over: this branch already has a component
 * by that name (a three-pillar layout, currently unused), and the two are
 * unrelated beyond the filename.
 */
import { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import {
  Users, DollarSign, ClipboardCheck, Wrench, Package,
  FileText, Briefcase, Megaphone, UserCheck,
  Check, Zap, Shield, Globe,
} from "lucide-react";

/* ─── Spring config ─── */
const SMOOTH = { stiffness: 100, damping: 30, restDelta: 0.001 };

/* ─── Canvas tokens ───
   The same two families and the same alpha ramps the rest of /solutions uses.
   Nothing here resolves a colour itself: every light-mode value is a custom
   property from the dcv2 palette, so the section themes with the page. The one
   literal is the cyan bloom, which is theme-independent on this route by
   design — Solutions.tsx draws its own blooms from the same rgb(56,229,255). */
const SANS = "'Manrope', sans-serif";
const MONO = "'Manrope', sans-serif";
const HAIRLINE = "1px solid rgba(var(--ink-rgb),.13)";

/* Nine deliberately unsynchronised pulse periods, in the canvas's manner: the
   status dots never line up into one collective blink. */
const DOT_ANIM = [
  "dcv2-pulseDot 2.6s ease-in-out infinite",
  "dcv2-pulseDot 3.2s .2s ease-in-out infinite",
  "dcv2-pulseDot 3s .4s ease-in-out infinite",
  "dcv2-pulseDot 2.4s ease-in-out infinite",
  "dcv2-pulseDot 2.8s .8s ease-in-out infinite",
  "dcv2-pulseDot 3.4s .3s ease-in-out infinite",
  "dcv2-pulseDot 2.5s .6s ease-in-out infinite",
  "dcv2-pulseDot 3.1s .1s ease-in-out infinite",
  "dcv2-pulseDot 2.7s .5s ease-in-out infinite",
];

/* ─── Portal Data — enriched content ─── */
const PORTALS = [
  {
    icon: UserCheck,
    label: "Employee Portal",
    tagline: "Empower your workforce",
    dept: "People Ops",
    desc: "A unified self-service hub where employees manage their entire work life, from submitting leave requests and accessing payslips to enrolling in benefits and staying current with company announcements.",
    features: ["Leave Management", "Payslip Access", "Benefits Enrollment", "Employee Directory"],
    highlight: "Reduces HR ticket volume by automating routine employee requests end-to-end.",
  },
  {
    icon: Users,
    label: "Manager Portal",
    tagline: "Lead with real-time visibility",
    dept: "Leadership",
    desc: "A comprehensive oversight dashboard giving managers the tools to approve requests, track team performance, manage schedules, and allocate resources, all from a single interface.",
    features: ["Team Approvals", "Performance Reviews", "Shift Scheduling", "Resource Reports"],
    highlight: "Gives managers full team visibility without switching between multiple tools.",
  },
  {
    icon: DollarSign,
    label: "Accounting Portal",
    tagline: "Financial precision, automated",
    dept: "Finance",
    desc: "Your financial operations center. It handles invoicing, bank reconciliation, expense tracking, and automated journal entries with audit-ready compliance built into every transaction.",
    features: ["Invoice Processing", "Bank Reconciliation", "Expense Tracking", "GL Entries"],
    highlight: "Automated reconciliation catches discrepancies before they become problems.",
  },
  {
    icon: ClipboardCheck,
    label: "Task Portal",
    tagline: "Intelligent task orchestration",
    dept: "Operations",
    desc: "AI-powered task management that automatically prioritizes queues, routes work across departments, monitors SLA compliance, and assigns tasks to the right person at the right time.",
    features: ["Smart Queues", "Workflow Builder", "SLA Monitoring", "Auto-Assignment"],
    highlight: "AI prioritization ensures critical tasks are always handled first.",
  },
  {
    icon: Wrench,
    label: "Internal Tools",
    tagline: "Your custom utility suite",
    dept: "Engineering",
    desc: "A flexible toolkit for internal operations: admin dashboards, configuration panels, system health monitors, and an API playground for rapid prototyping and debugging.",
    features: ["Admin Dashboards", "Config Manager", "Health Monitor", "API Explorer"],
    highlight: "Build and deploy internal tools without waiting for engineering sprints.",
  },
  {
    icon: Package,
    label: "Inventory Portal",
    tagline: "Supply chain intelligence",
    dept: "Logistics",
    desc: "Real-time asset and inventory management. Track stock levels, automate procurement workflows, manage supplier relationships, and get predictive supply chain analytics.",
    features: ["Stock Tracking", "Auto-Procurement", "Supplier Management", "Predictive Analytics"],
    highlight: "Predictive restocking prevents stockouts before they impact operations.",
  },
  {
    icon: FileText,
    label: "Document Portal",
    tagline: "Enterprise knowledge, organized",
    dept: "Knowledge Mgmt",
    desc: "A centralized knowledge base with version-controlled documents, AI-powered semantic search, compliance archiving, and smart templates that learn from your organization's patterns.",
    features: ["Version Control", "AI Search", "Compliance Archive", "Smart Templates"],
    highlight: "AI search understands intent, so you find any document in seconds, not minutes.",
  },
  {
    icon: Briefcase,
    label: "HR Portal",
    tagline: "People-first automation",
    dept: "Human Resources",
    desc: "End-to-end HR management covering the full employee lifecycle, from recruitment and applicant tracking to onboarding flows, training programs, and real-time culture analytics.",
    features: ["Recruitment ATS", "Onboarding Flows", "Training LMS", "Culture Pulse"],
    highlight: "Automated onboarding cuts new-hire setup time from days to hours.",
  },
  {
    icon: Megaphone,
    label: "Marketing Portal",
    tagline: "Campaigns that convert",
    dept: "Marketing",
    desc: "A unified campaign command center. Plan multi-channel campaigns, schedule content across platforms, score and nurture leads, and measure ROI with real-time attribution analytics.",
    features: ["Campaign Builder", "Content Calendar", "Lead Scoring", "ROI Dashboard"],
    highlight: "Unified attribution tracking shows exactly which campaigns drive revenue.",
  },
];

const TOTAL_CARDS = PORTALS.length;

function useResponsiveCard() {
  const [dims, setDims] = useState({ cardWidth: 380, cardGap: 24, vpWidth: typeof window !== "undefined" ? window.innerWidth : 1920 });

  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      const cardWidth = w < 480 ? 280 : w < 768 ? 320 : 380;
      const cardGap = w < 480 ? 14 : w < 768 ? 18 : 24;
      setDims({ cardWidth, cardGap, vpWidth: w });
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return dims;
}

/* ═════════════════════════════════════════════════════
   MAIN COMPONENT — Horizontal Scroll Carousel
   ═════════════════════════════════════════════════════ */
export default function PortalCarousel() {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { cardWidth, cardGap, vpWidth } = useResponsiveCard();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, SMOOTH);

  const totalStripWidth = TOTAL_CARDS * (cardWidth + cardGap);
  const viewportOffset = vpWidth / 2 - cardWidth / 2;
  const maxTravel = totalStripWidth - cardWidth - cardGap;
  const stripX = useTransform(smoothProgress, [0, 1], [viewportOffset, -maxTravel + viewportOffset]);

  const bgGridY = useTransform(smoothProgress, [0, 1], [0, -80]);
  const sectionFade = useTransform(smoothProgress, [0, 0.02, 0.95, 1], [0.5, 1, 1, 0.5]);

  return (
    <section
      ref={sectionRef}
      id="portals"
      style={{
        position: "relative",
        /* No ground of its own. The neighbours on either side — the hero above
           and the pillar bands below — both sit on the page's paper, so the
           band inherits it and the rhythm reads as one page. */
        /* The tall runway is what the sticky viewport scrubs against. With
           reduced motion there is nothing to scrub, so the section takes its
           natural height instead of reserving nine screens of scroll. */
        height: reduced ? "auto" : `${TOTAL_CARDS * 60}vh`,
      }}
    >
      {/* Dot field — the dark sections' texture, drawn in ink alpha so it
          survives both themes, masked to an ellipse and parallaxed off the
          same MotionValue the grid used. */}
      <motion.div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          willChange: "transform",
          y: reduced ? 0 : bgGridY,
          backgroundImage:
            "radial-gradient(rgba(var(--ink-rgb),.16) 1px, transparent 1.6px)",
          backgroundSize: "28px 28px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 50%, black, transparent)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 50%, black, transparent)",
        }}
      />

      {/* Sticky viewport */}
      <div
        style={
          reduced
            ? { display: "flex", flexDirection: "column", overflow: "hidden", padding: "16px 0" }
            : {
                position: "sticky",
                top: 0,
                height: "100vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }
        }
      >
        {/* Two cyan blooms, the page's own decoration. Held inside the sticky
            viewport rather than on the 540vh section so they stay behind the
            strip for the whole ride instead of scrolling away at the top. */}

        {/* Header — fixed at top */}
        <motion.div
          style={{
            position: "relative",
            zIndex: 30,
            flexShrink: 0,
            textAlign: "center",
            padding: "clamp(40px, 6vw, 64px) 20px clamp(16px, 2vw, 24px)",
            opacity: sectionFade,
          }}
        >
          <div
            style={{
              fontFamily: MONO,
              fontSize: 11.5,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "var(--link)",
              marginBottom: 18,
            }}
          >
            <span className="dc-mark" aria-hidden="true" /> The HiveMind Platform
          </div>

          <h2
            style={{
              margin: "0 0 14px",
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: "clamp(28px, 3.4vw, 46px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.14,
            }}
          >
            <span>One Platform. </span>
            <span style={{ color: "var(--acc2)" }}>Every Portal.</span>
          </h2>

          <p
            style={{
              margin: "0 auto",
              maxWidth: "52ch",
              fontSize: 16,
              color: "rgba(var(--ink-rgb),.66)",
            }}
          >
            Scroll to explore nine specialized portals connected by HiveMind AI.
          </p>
        </motion.div>

        {/* Card strip — fills remaining space, vertically centered */}
        <div
          style={
            reduced
              ? {
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  overflowX: "auto",
                  padding: "16px 24px",
                }
              : { position: "relative", flex: 1, display: "flex", alignItems: "center" }
          }
        >
          <motion.div
            style={{
              position: "relative",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              willChange: "transform",
              x: reduced ? 0 : stripX,
              gap: cardGap,
            }}
          >
          {PORTALS.map((portal, i) => (
            <PortalCard
              key={portal.label}
              {...portal}
              index={i}
              total={TOTAL_CARDS}
              cardWidth={cardWidth}
              scrollProgress={smoothProgress}
              reduced={!!reduced}
            />
          ))}
          </motion.div>
        </div>

        {/* Progress bar */}
        <div
          hidden={!!reduced}
          style={{
            position: "absolute",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 30,
          }}
        >
          <div
            style={{
              width: 192,
              height: 2,
              borderRadius: 999,
              overflow: "hidden",
              background: "rgba(var(--ink-rgb),.13)",
            }}
          >
            <motion.div
              style={{
                height: "100%",
                borderRadius: 999,
                background: "var(--acc2)",
                width: useTransform(smoothProgress, [0, 1], ["0%", "100%"]),
              }}
            />
          </div>
        </div>

        {/* Edge fade masks */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: "clamp(64px, 12vw, 160px)",
            pointerEvents: "none",
            zIndex: 20,
            background: "linear-gradient(90deg, var(--paper), transparent)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: "clamp(64px, 12vw, 160px)",
            pointerEvents: "none",
            zIndex: 20,
            background: "linear-gradient(270deg, var(--paper), transparent)",
          }}
        />
      </div>
    </section>
  );
}

/* ═════════════════════════════════════════════════════
   PORTAL CARD — Rich, professional layout
   ═════════════════════════════════════════════════════ */
function PortalCard({ icon: Icon, label, tagline, dept, desc, features, highlight, index, total, cardWidth, scrollProgress, reduced }: {
  icon: typeof Users; label: string; tagline: string; dept: string; desc: string;
  features: string[]; highlight: string; index: number; total: number; cardWidth: number;
  scrollProgress: any; reduced: boolean;
}) {
  const cardCenter = index / (total - 1);
  const halfFocal = 0.06;

  /* The card's identity, carried by form rather than hue: a mono index and a
     status dot that alternates disc / square. */
  const n = String(index + 1).padStart(2, "0");
  const dotRadius = index % 2 === 0 ? "50%" : "2px";

  /* Scale: 0.75 → 1.15 */
  const rawScale = useTransform(scrollProgress, (p: number) => {
    /* Reduced motion: every card at full size. Held here rather than at the
       binding site so the value stays a MotionValue. */
    if (reduced) return 1;
    const dist = Math.abs(p - cardCenter);
    const t = Math.max(0, 1 - dist / (halfFocal * 3));
    return 0.75 + t * 0.4;
  });
  const scale = useSpring(rawScale, SMOOTH);

  /* Opacity: 0.3 → 1 */
  const rawOpacity = useTransform(scrollProgress, (p: number) => {
    /* Reduced motion: none of them dimmed. Held here rather than at the
       binding site so the value stays a MotionValue. */
    if (reduced) return 1;
    const dist = Math.abs(p - cardCenter);
    const t = Math.max(0, 1 - dist / (halfFocal * 3));
    return 0.3 + t * 0.7;
  });
  const opacity = useSpring(rawOpacity, SMOOTH);

  /* Detail visibility */
  const rawDetailOpacity = useTransform(scrollProgress, (p: number) => {
    /* Reduced motion: detail already open. Held here rather than at the
       binding site so the value stays a MotionValue. */
    if (reduced) return 1;
    const dist = Math.abs(p - cardCenter);
    if (dist < halfFocal) return 1;
    if (dist < halfFocal * 2.5) return Math.max(0, 1 - (dist - halfFocal) / (halfFocal * 1.5));
    return 0;
  });
  const detailOpacity = useSpring(rawDetailOpacity, SMOOTH);

  /* Detail slide */
  const rawDetailY = useTransform(scrollProgress, (p: number) => {
    /* Reduced motion: detail not offset. Held here rather than at the
       binding site so the value stays a MotionValue. */
    if (reduced) return 0;
    const dist = Math.abs(p - cardCenter);
    const t = Math.max(0, 1 - dist / (halfFocal * 2.5));
    return 16 * (1 - t);
  });
  const detailY = useSpring(rawDetailY, SMOOTH);

  /* Glow */
  const rawGlow = useTransform(scrollProgress, (p: number) => {
    /* Reduced motion: glow at rest. Held here rather than at the
       binding site so the value stays a MotionValue. */
    if (reduced) return 1;
    const dist = Math.abs(p - cardCenter);
    return Math.max(0, 1 - dist / (halfFocal * 2));
  });
  const glowIntensity = useSpring(rawGlow, SMOOTH);

  /* Z-index */
  const zIndex = useTransform(scrollProgress, (p: number) => {
    const dist = Math.abs(p - cardCenter);
    return dist < halfFocal * 2 ? 10 : 1;
  });

  return (
    <motion.div
      style={{
        position: "relative",
        flexShrink: 0,
        willChange: "transform",
        width: cardWidth,
        scale,
        opacity,
        zIndex,
      }}
    >
      {/* Glow halo — the section's one accent, brightening as the card takes
          focus. It was nine hues; it is now the cyan the whole page uses. */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", inset: -16, pointerEvents: "none" }}
      >
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 24,
            opacity: glowIntensity,
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(56,229,255,.16), transparent 70%)",
            boxShadow:
              "0 0 50px rgba(56,229,255,.1), 0 0 100px rgba(56,229,255,.05)",
            filter: "blur(12px)",
          }}
        />
      </div>

      <div
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 18,
          background: "var(--paper2)",
          border: HAIRLINE,
          boxShadow: "0 20px 40px rgba(9,9,7,.07)",
        }}
      >
        {/* Top accent bar */}
        <div
          aria-hidden="true"
          style={{
            height: 3,
            width: "100%",
            opacity: 0.7,
            background:
              "linear-gradient(90deg, transparent 10%, var(--acc2) 50%, transparent 90%)",
          }}
        />

        {/* Shimmer — fades out when focused */}
        <motion.div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(105deg, transparent 40%, rgba(56,229,255,.07) 50%, transparent 60%)",
            backgroundSize: "200% 100%",
            animation: `shimmer ${3 + index * 0.3}s ease-in-out infinite`,
            opacity: useTransform(glowIntensity, (v: number) => 1 - v),
          }}
        />

        {/* Focus ring */}
        <motion.div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 18,
            pointerEvents: "none",
            opacity: glowIntensity,
            boxShadow: "inset 0 0 0 1px rgba(56,229,255,.3)",
          }}
        />

        <div style={{ position: "relative", padding: 24 }}>
          {/* ─── HEADER ─── */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(var(--ink-rgb),.05)",
                border: HAIRLINE,
                color: "var(--link)",
              }}
            >
              <Icon size={19} aria-hidden="true" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {label}
                </h3>
                <span
                  style={{
                    marginLeft: "auto",
                    fontFamily: MONO,
                    fontSize: 12,
                    color: "var(--link)",
                  }}
                >
                  {n}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: 9.5,
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 999,
                    background: "rgba(var(--ink-rgb),.06)",
                    color: "rgba(var(--ink-rgb),.62)",
                  }}
                >
                  {dept}
                </span>
                <span style={{ fontSize: 12.5, color: "var(--link)" }}>
                  {tagline}
                </span>
              </div>
            </div>
          </div>

          {/* ─── ALWAYS-VISIBLE STATUS ─── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              marginBottom: 16,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                flexShrink: 0,
                borderRadius: dotRadius,
                background: "var(--acc2)",
                animation: DOT_ANIM[index % DOT_ANIM.length],
              }}
            />
            <span
              style={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "var(--link)",
              }}
            >
              Operational
            </span>
            <div
              aria-hidden="true"
              style={{ flex: 1, height: 1, background: "rgba(var(--ink-rgb),.13)" }}
            />
            <Shield size={13} aria-hidden="true" style={{ color: "rgba(var(--ink-rgb),.62)" }} />
            <Globe size={13} aria-hidden="true" style={{ color: "rgba(var(--ink-rgb),.62)" }} />
          </div>

          {/* ─── DETAIL SECTION — visible when focused ─── */}
          <motion.div style={{ opacity: detailOpacity, y: detailY }}>
            {/* Description */}
            <p
              style={{
                margin: "0 0 18px",
                fontSize: 13.5,
                lineHeight: 1.7,
                color: "rgba(var(--ink-rgb),.66)",
              }}
            >
              {desc}
            </p>

            {/* Divider */}
            <div
              aria-hidden="true"
              style={{
                height: 1,
                width: "100%",
                marginBottom: 16,
                background: "rgba(var(--ink-rgb),.13)",
              }}
            />

            {/* Feature Grid — 2×2 */}
            <p
              style={{
                margin: "0 0 12px",
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "rgba(var(--ink-rgb),.62)",
              }}
            >
              Key Capabilities
            </p>
            <div
              style={{
                display: "grid",
                /* minmax(0, 1fr), not 1fr: on the 280px phone card a label
                   such as "Recruitment ATS" outgrew its half and pushed the
                   grid past the card's clipped edge. Now it wraps (T101026002). */
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 8,
                marginBottom: 18,
              }}
            >
              {features.map((f) => (
                <div
                  key={f}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "9px 11px",
                    borderRadius: 10,
                    background: "rgba(var(--ink-rgb),.04)",
                    border: HAIRLINE,
                  }}
                >
                  <Check
                    size={12}
                    aria-hidden="true"
                    style={{ flexShrink: 0, color: "var(--link)" }}
                  />
                  <span style={{ fontSize: 11.5, color: "rgba(var(--ink-rgb),.72)" }}>
                    {f}
                  </span>
                </div>
              ))}
            </div>

            {/* Highlight callout */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 11,
                padding: "12px 14px",
                borderRadius: 12,
                background: "rgba(56,229,255,.06)",
                border: "1px solid rgba(56,229,255,.22)",
              }}
            >
              <Zap
                size={15}
                aria-hidden="true"
                style={{ flexShrink: 0, marginTop: 2, color: "var(--acc2)" }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: "rgba(var(--ink-rgb),.72)",
                }}
              >
                {highlight}
              </p>
            </div>

            {/* CTA */}
          
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
